/* eslint-disable sort-keys */
import upperFirst from 'lodash/upperFirst';
import { useTranslate } from '@im/react-localization';
import { Option } from '@im/ui-lib/SelectOption';
import { atom, useRecoilState, useSetRecoilState } from 'recoil';

export type CreateNewEndUserFormData = Partial<
  Record<CreateNewEndUserFormKeys, string>
>;
const CreateNewEndUserFormAtom = atom<CreateNewEndUserFormData | undefined>({
  default: undefined,
  key: 'create-new-end-user',
});

const CreateNewEndUserFormIsUpdatedAtom = atom<boolean>({
  default: false,
  key: 'create-new-end-user-updated',
});

export function useCreateNewEndUserForm() {
  const [fromData, _setFormData] = useRecoilState(CreateNewEndUserFormAtom);
  const setIsFormUpdated = useSetRecoilState(CreateNewEndUserFormIsUpdatedAtom);

  const setFormData = (formData: Parameters<typeof _setFormData>[0]) => {
    setIsFormUpdated(Boolean(formData));
    _setFormData(formData);
  };
  return [fromData ?? {}, setFormData] as const;
}

const CreateNewEndUserFormErrorAtom = atom<
  CreateNewEndUserFormData | undefined
>({
  default: undefined,
  key: 'create-new-end-user-error',
});

const CreateNewEndUserFormErrorIsUpdatedAtom = atom<boolean>({
  default: false,
  key: 'create-new-end-user-error-updated',
});

export function useCreateNewEndUserFormError() {
  const [fromError, _setFormError] = useRecoilState(
    CreateNewEndUserFormErrorAtom
  );
  const setIsFormUpdated = useSetRecoilState(
    CreateNewEndUserFormErrorIsUpdatedAtom
  );

  const setFormError = (formData: Parameters<typeof _setFormError>[0]) => {
    setIsFormUpdated(Boolean(formData));
    _setFormError(formData);
  };
  return [fromError ?? {}, setFormError] as const;
}

export type FormField = {
  /**
   * @default 'text'
   */
  disabled?: boolean;
  fieldType?: 'text' | 'number';
  headerValue?: string;
  isHeader?: boolean;
  multiline?: boolean;
  name: string;
  options?: Option[];
  placeholder?: string;
  required?: boolean;
  visibleToBusinessType: string;
};

export type FormError = Record<CreateNewEndUserFormKeys, string>;

export type CreateNewEndUserFields = {
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  billingContactName: string;
  billingEmail: string;
  billingPhoneNumber: string;
  city: string;
  contactName: string;
  countryCode: string;
  customAccountId: string;
  email: string;
  endUserName: string;
  marketSegmentType: string;
  phoneNumber: string;
  postalCode: string;
  state: string;
  technicalContactName: string;
  technicalEmail: string;
  technicalPhoneNumber: string;
};
export type CreateNewEndUserFormKeys = keyof Partial<CreateNewEndUserFields>;

function useMarketSegmentType(): Option[] {
  const translate = useTranslate();
  return ['C', 'F', 'H', 'I', 'K', 'S', 'T', 'A', 'D', 'L', 'M', 'N', 'P'].map(
    (value) => ({
      value,
      label: translate(`VendorDeals.EndUserSegmentation${value}`),
    })
  );
}

export function useCreateNewEndUserFormFields(
  businessType: string | null,
  isUserNameDisabled = false,
  isHybrid = false
): Partial<Record<CreateNewEndUserFormKeys, FormField>> {
  const translate = useTranslate();
  const marketSegmentType = useMarketSegmentType();

  return Object.entries({
    endUserName: {
      required: true,
      visibleToBusinessType: 'All',
      disabled: isUserNameDisabled,
    },
    marketSegmentType: {
      required: true,
      options: marketSegmentType,
      visibleToBusinessType: 'All',
    },
    contactName: {
      required: businessType === 'Subscription' || isHybrid,
      visibleToBusinessType: 'All',
    },
    email: {
      required: businessType === 'Subscription' || isHybrid,
      visibleToBusinessType: 'All',
    },
    phoneNumber: {
      fieldType: 'number',
      required: businessType === 'Subscription' || isHybrid,
      visibleToBusinessType: 'All',
    },
    addressLine1: {
      required: true,
      visibleToBusinessType: 'All',
    },
    addressLine2: {
      visibleToBusinessType: 'All',
    },
    addressLine3: {
      visibleToBusinessType: 'All',
    },
    city: {
      required: true,
      visibleToBusinessType: 'All',
    },
    state: {
      required: true,
      visibleToBusinessType: 'All',
    },
    postalCode: {
      required: true,
      visibleToBusinessType: 'All',
    },
    countryCode: {
      required: true,
      visibleToBusinessType: 'All',
    },
    customAccountId: {
      visibleToBusinessType: 'Subscription',
    },
    billingContactName: {
      isHeader: true,
      headerValue: 'Billing contact',
      required: true,
      visibleToBusinessType: 'Subscription',
    },
    billingEmail: {
      required: true,
      visibleToBusinessType: 'Subscription',
    },
    billingPhoneNumber: {
      fieldType: 'number',
      required: true,
      visibleToBusinessType: 'Subscription',
    },
    technicalContactName: {
      isHeader: true,
      headerValue: 'Technical contact',
      required: true,
      visibleToBusinessType: 'Subscription',
    },
    technicalEmail: {
      required: true,
      visibleToBusinessType: 'Subscription',
    },
    technicalPhoneNumber: {
      fieldType: 'number',
      required: true,
      visibleToBusinessType: 'Subscription',
    },
  }).reduce(
    (acc, [key, attrs]) => ({
      ...acc,
      [key]: {
        ...attrs,
        name: translate(`VendorDeals.EndUser${upperFirst(key)}Name`),
        placeholder: translate(
          `VendorDeals.EndUser${upperFirst(key)}Placeholder`
        ),
      },
    }),
    {}
  );
}
